/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **The sentences a surface uses to refuse a capability, where more than one surface refuses it for
 * the same reason** (`#1179`, `#1187`).
 *
 * *Shared logic is not shared words.* Two surfaces make the identical decision — a query parameter
 * cannot keep a type — and each spelled it out for itself: `RequestParameterEditor` and
 * the round override editor carried the same refusal as two string literals, so shortening it in one
 * place would have left the long one behind on the other. A duplicated **matcher** eventually
 * produces a different value that something downstream catches; a duplicated **explanation**
 * produces a different sentence that nothing checks, read at the moment an operator is most likely
 * to believe it.
 *
 * ## Why this is not `mapping-help.ts`
 *
 * That file's line is `X28b`'s and it is load-bearing: **teaching text lives behind the `?`, a
 * refusal never does.** A disabled control whose reason is behind a hover is an absent control
 * again. These strings render **on the surface**, always visible, beside the control they are
 * about — so they are a different kind of thing that happens also to be shared, and filing them
 * together would blur the one distinction `mapping-help.ts` exists to hold.
 *
 * ## They name the destination, not the mechanism
 *
 * Shortened 2026-09-04 on Todd's ruling — *"I think it can be very simple: `A [type] is always text`
 * … the shorter the better."* The previous form explained the consequence (*`?n=1` does not remember
 * that 1 was a number*), which is true and is one clause more than a refusal needs: the operator is
 * being turned away, and the shortest true sentence is the one they will actually read.
 */

/** A query parameter is text on the wire, so it cannot keep a scalar type. */
export const QUERY_PARAM_IS_TEXT = 'A query parameter is always text.';

/** A header is text on the wire, whatever the value resolved to. */
export const HEADER_IS_TEXT = 'A header is always text.';
